import { ColorScheme } from "../../.storyblok/types/287059/storyblok-components";

/**
 * The color-scheme fields UI components actually render, named explicitly so
 * block prop types don't have to reuse the codegen'd `ColorScheme` type.
 * `fallbackColorScheme` below is a structural superset of this, so it can
 * still be passed anywhere either type is expected.
 */
export type ColorSchemeProps = {
  primaryColor: string;
  secondaryColor: string;
  textColor?: string;
  textAppearance: "light" | "dark";
  canShadowRGB: string;
  buttonColor: "brand" | "white";
};

/**
 *  1. this is just for internal sorting now, often a flavour is shared between markets
 *  and can be "summer", "white", "coconut".
 *  We rarely get a clear list of all editions and their naming, this just helps us keep track a little bit.
 */

/**
 * @deprecated Use ColorSchemeStory from Storyblok instead
 */
type ColorSchemeType = ColorScheme & {
  edition?: string[];
};

// If something in undefined, lets set a default
export const fallbackColorScheme: ColorSchemeType = {
  component: "colorScheme",
  name: "fallback",
  _uid: "6f105c80-1b4e-4b67-9d5c-9b4c5210b5b5",
  edition: [],
  primaryColor: "#282874",
  secondaryColor: "#efefef",
  textAppearance: "dark",
  canShadowRGB: "0 15 30",
  buttonColor: "brand",
};

const colorSchemes: ColorSchemeType[] = [
  {
    component: "colorScheme",
    name: "energy-drink-original",
    _uid: "373a0857-e83c-4601-bdc2-705da39efa56",
    edition: [],
    primaryColor: "#282874",
    secondaryColor: "#efefef",
    textAppearance: "dark",
    canShadowRGB: "44 44 78",
    buttonColor: "brand",
  },
  {
    component: "colorScheme",
    name: "energy-drink-sugarfree",
    _uid: "c9f3ca3a-3990-4365-95a2-f89102ec3d6f",
    edition: [],
    primaryColor: "#009EDF",
    secondaryColor: "#0096D4",
    textAppearance: "light",
    canShadowRGB: "0 63 89",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "energy-drink-zero",
    _uid: "e054ce7f-f70f-4f27-add5-602ef2476223",
    edition: [],
    primaryColor: "#B1D0EE",
    secondaryColor: "#A0C6EA",
    textAppearance: "dark",
    canShadowRGB: "66 104 140",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-acai-berry",
    _uid: "d430c955-cfe0-411c-9f29-e91934cc766c",
    edition: ["purple"],
    primaryColor: "#732c82",
    secondaryColor: "#6D2A7B",
    textAppearance: "light",
    canShadowRGB: "0 15 30",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-apricot-strawberry",
    _uid: "985c4268-209d-44bf-97ff-67484880e9a4",
    edition: ["apricot", "amber"],
    primaryColor: "#F3911B",
    secondaryColor: "#F28B0E",
    textAppearance: "dark",
    canShadowRGB: "103 58 5",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-blueberry",
    _uid: "892ac681-6e94-4c26-8e03-b1ef8f7b71b2",
    edition: ["blue"],
    primaryColor: "#282874",
    secondaryColor: "#26266E",
    textAppearance: "light",
    canShadowRGB: "0 53 80",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-cactus-fruit",
    _uid: "afeb4018-7151-4f93-8f3e-7cde118aa72a",
    edition: ["green"],
    primaryColor: "#2CA44A",
    secondaryColor: "#2A9C46",
    textAppearance: "dark",
    canShadowRGB: "0 15 30",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-coconut-berry",
    _uid: "25ec26f8-d11a-437f-86b3-709d1c6539f6",
    edition: ["white", "coconut"],
    primaryColor: "#0070B8",
    secondaryColor: "#006AAF",
    textAppearance: "light",
    canShadowRGB: "36 57 70",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-curuba-elderflower",
    _uid: "e6d26405-3f31-4684-8e7f-f66b43ef1df3",
    edition: ["green", "summer"],
    primaryColor: "#78B941",
    secondaryColor: "#72B03E",
    textAppearance: "light",
    canShadowRGB: "48 74 26",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-dragon-fruit",
    _uid: "791e1bff-a2d1-4dbe-b210-82c1e692847d",
    edition: ["green"],
    primaryColor: "#2CA44A",
    secondaryColor: "#2A9C46",
    textAppearance: "dark",
    canShadowRGB: "20 74 33",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-fig-apple",
    _uid: "bd0d8057-1338-4f05-b1c7-00a48cea927b",
    edition: ["winter"],
    primaryColor: "#037274",
    secondaryColor: "#0f5a5a",
    textAppearance: "light",
    canShadowRGB: "0 53 80",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-forest-fruits",
    _uid: "13cf199c-8a9e-424b-af5b-f8cc3c51423a",
    edition: ["pink"],
    primaryColor: "#e77bab",
    secondaryColor: "#e77bab", // intentionally the same as primary
    textAppearance: "dark",
    canShadowRGB: "0 15 30",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-grapefruit-blossom",
    _uid: "eaddd496-5027-4bad-ab24-796e861aa6cb",
    edition: ["spring"],
    primaryColor: "#7D62CE",
    secondaryColor: "#6952AD",
    textAppearance: "light",
    canShadowRGB: "0 15 30",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-juneberry",
    _uid: "35148f16-f934-415a-a380-ba45b29dbc39",
    edition: ["blue", "sea-blue"],
    primaryColor: "#0085C8",
    secondaryColor: "#007EBE",
    textAppearance: "light",
    canShadowRGB: "0 53 80",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-iced-vanilla-berry",
    _uid: "1326a31f-c525-4399-a3b7-b0d5de3f2f24",
    edition: ["winter"],
    primaryColor: "#53B2C2",
    secondaryColor: "#49ADBE",
    textAppearance: "light",
    canShadowRGB: "29 74 82",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-peach-nectarine",
    _uid: "3075bf08-dba6-45d5-bc05-0c8aec4b9759",
    edition: ["peach"],
    primaryColor: "#ff00ff",
    secondaryColor: "#8e101b",
    textAppearance: "dark",
    canShadowRGB: "0 15 30",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-spiced-pear",
    _uid: "eb02a817-679e-48ad-a3cb-84012bd75742",
    edition: ["ruby"],
    primaryColor: "#E6301F",
    secondaryColor: "#B50045",
    textAppearance: "light",
    canShadowRGB: "0 15 30",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-tropical",
    _uid: "2fd3e759-378c-41e7-8ecd-1d481deabb6a",
    edition: ["yellow"],
    primaryColor: "#FFCB04",
    secondaryColor: "#F6C300",
    textAppearance: "dark",
    canShadowRGB: "104 82 0",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-watermelon",
    _uid: "8d5e759f-54c4-43e9-ba50-3c3c8fcb257e",
    edition: ["red"],
    primaryColor: "#E6301F",
    secondaryColor: "#C53224",
    textAppearance: "light",
    canShadowRGB: "79 20 14",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-white-peach", // white-peach is the flavour
    _uid: "248bcee6-b876-4f54-8fa9-d05deb3f362a",
    edition: ["summer"],
    primaryColor: "#E24585",
    secondaryColor: "#E24585",
    textAppearance: "light",
    canShadowRGB: "0 15 30",
    buttonColor: "white",
  },
  {
    component: "colorScheme",
    name: "edition-fuji-apple-ginger",
    _uid: "f4c62962-d31a-4788-b9da-9a919cd92449",
    edition: ["winter"],
    primaryColor: "#BF1431",
    secondaryColor: "#AE132D",
    textAppearance: "light",
    canShadowRGB: "107 12 28",
    buttonColor: "white",
  },
];

/**
 * @deprecated: should only be used for testing/Storybook purposes. Color schemes
 * for Storyblok are no-longer hardcoded, they are another Story-type that is
 * usually a relationship of the product.
 */
export function getSchemeById(name?: string): ColorSchemeType {
  const selectedScheme =
    colorSchemes.find((theme) => theme.name === name) || fallbackColorScheme;

  return {
    ...selectedScheme,
  };
}
